
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, RefreshCw, Clock, Activity } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";

interface ActivityLogItem {
  id: string;
  user_id: string;
  action: string;
  details: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

interface ActivityLogProps {
  userId?: string; // If not provided, uses the current user
  limit?: number;
  showHeader?: boolean;
}

export function ActivityLog({ userId, limit = 10, showHeader = true }: ActivityLogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activities, setActivities] = useState<ActivityLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  const targetUserId = userId || user?.id;

  useEffect(() => {
    if (targetUserId) {
      fetchActivities();
    }
  }, [targetUserId, activeTab]);

  const fetchActivities = async () => {
    if (!targetUserId) return;

    try {
      setLoading(true);
      
      let query = supabase
        .from('activity_logs')
        .select('*')
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      // Apply filter based on active tab
      if (activeTab !== "all") {
        query = query.ilike('action', `${activeTab}%`);
      }
      
      const { data, error } = await query;

      if (error) {
        throw error;
      }

      setActivities(data || []);
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast({
        title: "Failed to load activity history",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (action: string) => {
    if (action.includes("login")) return <Clock className="h-4 w-4 text-blue-500" />;
    if (action.includes("profile")) return <Activity className="h-4 w-4 text-green-500" />;
    return <Activity className="h-4 w-4 text-slate-500" />;
  };

  const getActivityBadge = (action: string) => {
    let variant: "default" | "outline" | "secondary" | "destructive" = "default";
    
    if (action.includes("login")) variant = "secondary";
    if (action.includes("update")) variant = "outline";
    if (action.includes("delete")) variant = "destructive";
    
    return <Badge variant={variant} className="ml-2">{action}</Badge>;
  };

  const formatActivityDetails = (details: any) => {
    if (!details) return null;
    
    try {
      const detailsObj = typeof details === 'string' ? JSON.parse(details) : details;
      
      return (
        <div className="mt-1 text-xs text-slate-500 space-y-1">
          {Object.entries(detailsObj).map(([key, value]: [string, any]) => (
            <div key={key} className="grid grid-cols-5">
              <span className="col-span-1 font-medium">{key}</span>
              <span className="col-span-4 truncate">
                {typeof value === 'object' ? (
                  value.old !== undefined && value.new !== undefined ? (
                    <>
                      <span className="line-through mr-2">{String(value.old || '')}</span>
                      <span className="text-green-600">{String(value.new || '')}</span>
                    </>
                  ) : (
                    JSON.stringify(value)
                  )
                ) : (
                  String(value)
                )}
              </span>
            </div>
          ))}
        </div>
      );
    } catch (e) {
      console.error("Error parsing activity details:", e);
      return <span className="text-xs text-slate-500">{String(details)}</span>;
    }
  };

  return (
    <Card className="shadow-sm border-slate-200">
      {showHeader && (
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Activity History</CardTitle>
              <CardDescription>
                Recent account activity and changes
              </CardDescription>
            </div>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={fetchActivities}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
      )}
      
      <CardContent>
        <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="all">All Activity</TabsTrigger>
            <TabsTrigger value="login">Logins</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab} className="mt-0">
            {loading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="flex gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-3 w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Activity className="mx-auto h-12 w-12 text-slate-300 mb-3" />
                <p>No activity records found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div key={activity.id} className="flex gap-3 items-start pb-4 border-b border-slate-100 last:border-b-0">
                    <div className="mt-1">
                      {getActivityIcon(activity.action)}
                    </div>
                    <div>
                      <div className="flex items-center">
                        <span className="text-sm font-medium">
                          {activity.action.replace(/_/g, " ")}
                        </span>
                        {getActivityBadge(activity.action)}
                      </div>
                      
                      {formatActivityDetails(activity.details)}
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-xs text-slate-400 mt-2 cursor-help">
                              {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">
                              {format(new Date(activity.created_at), "PPpp")}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      {activity.ip_address && (
                        <p className="text-xs text-slate-400">
                          IP: {activity.ip_address}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
